import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Check, ChevronDown, Plus } from "lucide-react";
import { Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { useOrganization } from "../gen/rbt/cloud/v1alpha1/organization/organization_rbt_react";

interface OrganizationSelectorProps {
  organizationIds: string[];
  currentOrganizationId?: string;
  onOrganizationChange?: (organizationId: string) => void;
}

// Component that fetches and displays a single organization's name.
function OrgName({ organizationId }: { organizationId: string }) {
  const { useGet } = useOrganization({ id: organizationId });
  const { response } = useGet({}, { suspense: true });

  return <>{response?.name}</>;
}

// Component for a single organization menu item.
function OrgMenuItem({
  organizationId,
  isSelected,
  onSelect,
}: {
  organizationId: string;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const { useGet } = useOrganization({ id: organizationId });
  const { response } = useGet({}, { suspense: true });

  return (
    <DropdownMenuItem
      onClick={onSelect}
      className="flex items-center justify-between cursor-pointer"
    >
      <span className="font-medium">{response?.name}</span>
      {isSelected && <Check className="h-4 w-4" />}
    </DropdownMenuItem>
  );
}

export function OrganizationSelector({
  organizationIds,
  currentOrganizationId,
  onOrganizationChange,
}: OrganizationSelectorProps) {
  const navigate = useNavigate();

  // Use the first organization as current if none specified.
  const effectiveCurrentOrgId = currentOrganizationId ?? organizationIds[0];

  if (!effectiveCurrentOrgId || organizationIds.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-muted-foreground">Organization</span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="gap-2">
            <span className="font-medium">
              <OrgName
                key={effectiveCurrentOrgId}
                organizationId={effectiveCurrentOrgId}
              />
            </span>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[240px]">
          <DropdownMenuLabel>Organizations</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <Suspense
            fallback={
              <div className="px-2 py-1 text-sm text-muted-foreground">
                Loading...
              </div>
            }
          >
            {organizationIds.map((orgId) => (
              <OrgMenuItem
                key={orgId}
                organizationId={orgId}
                isSelected={effectiveCurrentOrgId === orgId}
                onSelect={() => onOrganizationChange?.(orgId)}
              />
            ))}
          </Suspense>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => navigate("/create-organization")}
            className="flex items-center gap-2 cursor-pointer"
          >
            <Plus className="h-4 w-4" />
            <span>Create new organization</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
